import React, { FC, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MuiContainer from '@mui/material/Container';
import { styled } from '@mui/system';
import MuiGrid from '@mui/material/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import TextField from 'src/components/TextField';
import translate from '@translate';
import style from './style';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link } from 'react-router-dom';
import { colors } from '../../themes/default';
import { checkFormValidity } from '../../utils/formFunctions';
import { loginFetch } from '../../resources/login/reducer';
import Loading from 'src/components/Loading/intex';
import appLogo from 'src/utils/appLogo';
import LangSelect from 'src/components/LangSelect';
import Fade from '@mui/material/Fade';
import { RootStoreType } from 'src/types/store';
import { EyeIcon } from 'src/components/SkladonIcon/SmallIcons';
import ForgetPassword from './ForgetPassword';
import { FORCE_LOGOUT_MESSAGE_SESSION_KEY } from 'src/utils/forceSessionLogout';
import { PASSWORD_CHANGED_MESSAGE_SESSION_KEY } from 'src/utils/passwordChangedLogout';

const TIMEOUT_ERROR_MESSAGE = 5000;
const initData = { username: '', password: '' };
const initErrors: { [key: string]: boolean } = {
    username: false,
    password: false,
};

export const LogoBlock = styled('div')({
    width: '100%',
    textAlign: 'center',
});
export const Logo = styled('img')({
    width: '90%',
    marginBottom: '15px',
    marginTop: '-20px',
});

const LoginContainer: FC = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state: RootStoreType) => state.login.loading);
    const callLoginError = useSelector((state: RootStoreType) => state.login.error);

    const formRef = useRef<HTMLFormElement>(null);
    const [editData, setEditData] = useState(initData);
    const [editErrors, setEditErrors] = useState(initErrors);
    const [visiblePassword, setVisiblePassword] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const [loginErrorTimeout, setloginErrorTimeout] = useState(-1);
    const [sessionExpiredMessage, setSessionExpiredMessage] = useState(false);
    const [passwordChangedMessage, setPasswordChangedMessage] = useState(false);
    const [openForgetPassword, setOpenForgetPassword] = useState<boolean>(false);

    useEffect(() => {
        const passwordChanged = Boolean(sessionStorage.getItem(PASSWORD_CHANGED_MESSAGE_SESSION_KEY));
        const sessionExpired = Boolean(sessionStorage.getItem(FORCE_LOGOUT_MESSAGE_SESSION_KEY));
        if (passwordChanged || sessionExpired) {
            sessionStorage.removeItem(PASSWORD_CHANGED_MESSAGE_SESSION_KEY);
            sessionStorage.removeItem(FORCE_LOGOUT_MESSAGE_SESSION_KEY);
            // password changed message wins over the generic session expired one
            if (passwordChanged) {
                setPasswordChangedMessage(true);
            } else {
                setSessionExpiredMessage(true);
            }

            const timeout = window.setTimeout((): void => {
                setSessionExpiredMessage(false);
                setPasswordChangedMessage(false);
            }, TIMEOUT_ERROR_MESSAGE);

            return () => {
                window.clearTimeout(timeout);
            };
        }
    }, []);

    useEffect(() => {
        if (callLoginError) {
            setLoginError(true);
            setloginErrorTimeout(
                window.setTimeout((): void => {
                    setLoginError(false);
                }, TIMEOUT_ERROR_MESSAGE)
            );
        } else {
            window.clearTimeout(loginErrorTimeout);
            setLoginError(false);
        }
    }, [callLoginError]);

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        let newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            dispatch(loginFetch(editData));
        }

        setEditErrors(newFormErrors);
    };

    const onChange = (key: 'username' | 'password', value: any, error: boolean) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    if (openForgetPassword) {
        return <ForgetPassword onClose={() => setOpenForgetPassword(false)} />;
    }

    return (
        <MuiContainer fixed maxWidth="xs" disableGutters sx={{ display: 'flex', height: '100%' }}>
            <div className="Login" style={style.app}>
                {loading && <Loading />}
                <form ref={formRef} onSubmit={handleSubmit}>
                    <MuiGrid container columns={1}>
                        <MuiGrid item xs={1}>
                            <LogoBlock>
                                <Logo src={appLogo} alt="skladon background" />
                            </LogoBlock>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <TextField
                                value={editData.username}
                                error={editErrors.username}
                                onChange={(value, error) => onChange('username', value, error)}
                                name="username"
                                label={translate('login.yourEmail')}
                                required
                                fullWidth
                            />
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <TextField
                                value={editData.password}
                                error={editErrors.password}
                                onChange={(value, error) => onChange('password', value, error)}
                                label={translate('login.password')}
                                name="password"
                                type={!visiblePassword ? 'password' : 'text'}
                                required
                                fullWidth
                                endIcon={{
                                    onClick: () => {
                                        setVisiblePassword(!visiblePassword);
                                    },
                                    icon: visiblePassword ? <VisibilityOff /> : <EyeIcon />,
                                }}
                            />
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <LangSelect />
                            <Typography variant="body2" gutterBottom>
                                {translate('login.forgetPassword')}
                                <Button
                                    variant="text"
                                    fullWidth
                                    sx={{
                                        textAlign: 'left',
                                        paddingLeft: '0px',
                                        display: 'inline-block',
                                        width: 'auto',
                                        textDecoration: 'underline',
                                    }}
                                    noBackground
                                    onClick={() => setOpenForgetPassword(true)}
                                >
                                    {translate('login.forgetPasswordClick')}
                                </Button>
                            </Typography>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            {/* <Button type="button" fullWidth onClick={handleSubmit}> */}
                            <Button type="submit" fullWidth>
                                {translate('login.logIn')}
                            </Button>
                        </MuiGrid>
                        <MuiGrid sx={{ mt: 2 }}>
                            <Fade in={passwordChangedMessage} timeout={{ enter: 1000, exit: 1000 }}>
                                <MuiContainer>
                                    <div>
                                        <Typography align="center" display="inline">
                                            {translate('login.passwordChanged')}
                                        </Typography>
                                    </div>
                                </MuiContainer>
                            </Fade>
                            <Fade in={sessionExpiredMessage} timeout={{ enter: 1000, exit: 1000 }}>
                                <MuiContainer>
                                    <div>
                                        <Typography align="center" display="inline">
                                            {translate('login.sessionExpired')}
                                        </Typography>
                                    </div>
                                </MuiContainer>
                            </Fade>
                            <Fade in={loginError} timeout={{ enter: 1000, exit: 1000 }}>
                                <MuiContainer>
                                    <div>
                                        <Typography align="center" display="inline">
                                            {translate('login.wrongLoginPassword')}
                                        </Typography>
                                    </div>
                                </MuiContainer>
                            </Fade>
                        </MuiGrid>
                    </MuiGrid>
                </form>
            </div>
        </MuiContainer>
    );
};

export default LoginContainer;
